import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import { fetchUserProfile } from '../../store/slices/userSlice';
import { fetchMyTeams } from '../../store/slices/teamSlice';
import { withdrawEnrollment } from '../../store/slices/enrollmentSlice';
import { logout, resendVerification, deleteAccount } from '../../store/slices/authSlice';
import { EnrollmentStatus } from '@sulis-liiga/common';
import { groupEnrollmentsByLadder } from '../../utils/enrollmentGrouping';
import { enrollmentStatusLabel, enrollmentStatusClassName } from '../../utils/enrollmentStatus';

const ProfilePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { profile, loading, error } = useSelector((state: RootState) => state.user);
  const { myTeams, loading: teamLoading } = useSelector((state: RootState) => state.team);

  const [verifyStatus, setVerifyStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/');
  };

  const handleResendVerification = async () => {
    if (!profile) {
      return;
    }
    setVerifyStatus('sending');
    setVerifyError(null);
    try {
      await dispatch(resendVerification(profile.email)).unwrap();
      setVerifyStatus('sent');
    } catch (err) {
      setVerifyStatus('error');
      setVerifyError(typeof err === 'string' ? err : 'Failed to send verification email.');
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await dispatch(deleteAccount()).unwrap();
      navigate('/');
    } catch (err) {
      setDeleting(false);
      setDeleteError(typeof err === 'string' ? err : 'Failed to delete account.');
    }
  };

  useEffect(() => {
    dispatch(fetchUserProfile());
    dispatch(fetchMyTeams());
  }, [dispatch]);

  const handleWithdrawEnrollment = async (enrollmentId: string) => {
    await dispatch(withdrawEnrollment(enrollmentId))
      .unwrap()
      .then(() => dispatch(fetchMyTeams()))
      .catch(() => {
        /* the enrollment slice surfaces the error; nothing further to do here */
      });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <svg
          className="animate-spin h-10 w-10 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white shadow rounded-lg p-6">
          <div className="rounded-md bg-danger-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-danger-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-danger-800">{error}</h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="md:flex md:items-center md:justify-between mb-8">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Profile
            </h2>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-2 md:mt-0 md:ml-4">
            <Link
              to="/profile/edit"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Edit Profile
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center px-4 py-2 border border-danger-600 rounded-md shadow-sm text-sm font-medium text-danger-600 hover:bg-danger-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-500"
            >
              Log out
            </button>
          </div>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <div className="flex items-center">
              {profile.profilePicture ? (
                <img
                  className="h-20 w-20 rounded-full object-cover"
                  src={profile.profilePicture}
                  alt={`${profile.firstName} ${profile.lastName}`}
                />
              ) : (
                <div className="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-2xl font-medium text-gray-500">
                    {profile.firstName[0]}
                    {profile.lastName[0]}
                  </span>
                </div>
              )}
              <div className="ml-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {profile.firstName} {profile.lastName}
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">{profile.email}</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Full name</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {profile.firstName} {profile.lastName}
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Email address</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <div className="flex items-center flex-wrap gap-2">
                    <span>{profile.email}</span>
                    {profile.emailVerified ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                        Unverified
                      </span>
                    )}
                  </div>
                  {!profile.emailVerified &&
                    (verifyStatus === 'sent' ? (
                      <p className="mt-2 text-sm text-green-700">
                        Verification email sent — check your inbox for the link.
                      </p>
                    ) : (
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={handleResendVerification}
                          disabled={verifyStatus === 'sending'}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                        >
                          {verifyStatus === 'sending' ? 'Sending...' : 'Verify email'}
                        </button>
                        {verifyError && (
                          <p className="mt-1 text-sm text-danger-700">{verifyError}</p>
                        )}
                      </div>
                    ))}
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Gender</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 capitalize">
                  {profile.gender}
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Birth year</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {profile.birthYear}
                </dd>
              </div>
              {profile.phone && (
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Phone number</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {profile.phone}
                  </dd>
                </div>
              )}
              {profile.address && (
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Address</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {profile.address.street && <div>{profile.address.street}</div>}
                    {(profile.address.city ||
                      profile.address.state ||
                      profile.address.postalCode) && (
                      <div>
                        {profile.address.city}
                        {profile.address.state && `, ${profile.address.state}`}{' '}
                        {profile.address.postalCode}
                      </div>
                    )}
                    {profile.address.country && <div>{profile.address.country}</div>}
                  </dd>
                </div>
              )}
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Role</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                    {profile.roles.join(', ')}
                  </span>
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* My teams card */}
        <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex items-center justify-between">
            <h3 className="text-lg leading-6 font-medium text-gray-900">My Teams</h3>
            <Link
              to="/teams/new"
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              + Create team
            </Link>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            {teamLoading && myTeams.length === 0 ? (
              <p className="text-sm text-gray-500">Loading teams…</p>
            ) : myTeams.length === 0 ? (
              <p className="text-sm text-gray-500">
                You are not on any teams yet.{' '}
                <Link to="/teams/new" className="text-primary-600 hover:underline">
                  Create a team
                </Link>{' '}
                to get started.
              </p>
            ) : (
              <div className="space-y-4">
                {myTeams.map(team => (
                  <div key={team.id} className="border border-gray-100 rounded-lg overflow-hidden">
                    {/* Team header */}
                    <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
                      <div>
                        <Link
                          to={`/teams/${team.id}`}
                          className="text-sm font-semibold text-gray-900 hover:text-primary-600"
                        >
                          {team.name}
                        </Link>
                        <span className="ml-2 text-xs text-gray-500 capitalize">{team.type}</span>
                      </div>
                      {team.status && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">
                          {team.status}
                        </span>
                      )}
                    </div>

                    {/* Enrollments — one row per division ladder, across seasons */}
                    {team.enrollments && team.enrollments.length > 0 ? (
                      <ul className="divide-y divide-gray-100">
                        {groupEnrollmentsByLadder(team.enrollments).map(enrollment => (
                          <li
                            key={enrollment.id}
                            className="px-4 py-3 flex items-center justify-between"
                          >
                            <div>
                              <p className="text-sm text-gray-900">
                                {enrollment.divisionBaseName ?? enrollment.divisionGroupId}
                                {enrollment.divisionName ? (
                                  <span className="text-gray-500 font-normal">
                                    {' '}
                                    &middot; {enrollment.divisionName}
                                  </span>
                                ) : null}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {enrollment.category}
                                {' · '}
                                <span className={enrollmentStatusClassName(enrollment.status)}>
                                  {enrollmentStatusLabel(enrollment.status)}
                                </span>
                              </p>
                            </div>
                            {enrollment.status !== EnrollmentStatus.WITHDRAWN && (
                              <button
                                type="button"
                                onClick={() => handleWithdrawEnrollment(enrollment.id)}
                                className="text-xs px-2 py-1 rounded-md text-danger-600 border border-danger-300 hover:bg-danger-50"
                              >
                                Withdraw
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        Not enrolled in any division.{' '}
                        <Link to="/leagues" className="text-primary-600 hover:underline">
                          Browse leagues
                        </Link>
                        .
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {profile.preferences && (
          <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">My Preferences</h3>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Pending-action emails</dt>
                  {profile.emailVerified ? (
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {profile.preferences.emailOnPendingAction ? 'Enabled' : 'Disabled'}
                    </dd>
                  ) : (
                    // Not active until the email is verified — shown greyed out. It
                    // auto-enables (it is on by default) once verification completes.
                    <dd className="mt-1 text-sm text-gray-400 sm:mt-0 sm:col-span-2">
                      Disabled — verify your email address to enable this
                    </dd>
                  )}
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Auto-approve results</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {profile.preferences.autoApproveResults ? 'Enabled' : 'Disabled'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}

        {/* Danger zone — GDPR right to be forgotten */}
        <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg border border-danger-200">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-danger-800">Delete account</h3>
            <p className="mt-1 text-sm text-gray-500">
              Permanently erase your account and personal data (your right to be forgotten). Your
              name is removed from teams, games and standings — a team you share with another player
              keeps that player&apos;s name. This cannot be undone.
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-4 sm:px-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleDeleteAccount}
              disabled={deleting}
              className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-danger-600 hover:bg-danger-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-500 disabled:opacity-50"
              aria-label={
                deleteConfirm ? 'Confirm permanent account deletion' : 'Delete my account'
              }
            >
              {deleting
                ? 'Deleting…'
                : deleteConfirm
                ? 'Confirm — permanently delete'
                : 'Delete my account'}
            </button>
            {deleteConfirm && !deleting && (
              <button
                type="button"
                onClick={() => setDeleteConfirm(false)}
                className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
            {deleteError && <p className="text-sm text-danger-700 w-full">{deleteError}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
